import { Box, Button, Card, IconButton, Table, TableBody, TableCell, TableContainer, TablePagination, TableRow, Tooltip, Typography, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import api from '../../../service/api'
import TableHeader from '../../common/TableHeader'
import { formatDate } from '../../../utils/utilities'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import DataLoadingSpinner from '../../common/DataLoadingSpinner'
import AssignmentViewer from '../../common/AssignmentViewer'
import { Analytics, OpenInNew } from '@mui/icons-material'
import SubmissionsViewer from './SubmissionsViewer'
import noAssignmentImg from '../../../assets/noassignment.svg'
import TryAgainMessageBlock from '../../common/TryAgainMessageBlock'

const columns =
    [
        { id: 2, name: "Assignment name", minWidth: "150px", padding: "10px 20px 10px 40px" },
        { id: 3, name: 'Faculty name', minWidth: "150px", padding: "10px 20px" },
        { id: 6, name: 'DEPARTMENT', minWidth: "250px", padding: "10px 20px" },
        { id: 4, name: "Subject", minWidth: "250px", padding: "10px 20px" },
        { id: 5, align: "center", name: "Created on", minWidth: "150px", padding: "10px 20px" },
        { id: 7, align: "center", name: "Submissions", minWidth: "150px", padding: "10px 20px" },
        { id: 7, align: "right", name: "Actions", minWidth: "150px", padding: "10px 40px 10px 20px" },
    ]

const AssignmentList = () => {

    const [searchParams, setSearchParams] = useSearchParams( { searchText: "", month: "", selectedDepartment: "all", selectedInstitute: "all", view: "tabular", sortByName: "1", sortBySubmissionsCount: "1", sortByAssignmentCount: "1" } )
    const searchText = searchParams.get( 'searchText' ) || ""
    const selectedInstitute = searchParams.get( 'selectedInstitute' ) || "all"
    const selectedDepartment = searchParams.get( 'selectedDepartment' ) || "all"

    const [page, setPage] = useState( 0 )
    const [error, setError] = useState( null )
    const [loading, setLoading] = useState( true )
    const [assignments, setAssignments] = useState( [] )
    const [rowsPerPage, setRowsPerPage] = useState( 50 )
    const [selectedAssignment, setSelectedAssignment] = useState( null )
    const [submissionViewerState, setSubmissionViewerState] = useState( false )

    const { table, palette } = useTheme()

    const navigate = useNavigate()


    const filterFunction = item => {
        const searchValue = new RegExp( searchText?.replace( /[.*+?^${}()|[\]\\]/g, '\\$&' ), 'i' )
        if ( selectedDepartment !== "all" ) {
            const [department_id, legacy_department_id] = selectedDepartment?.split( "-" )
            const doesMatchDepartment = ( item.legacy_system_data && +legacy_department_id === +item.department_id ) || ( !item.legacy_system_data && +department_id === +item.department_id )
            return doesMatchDepartment && ( item.department?.match( searchValue ) || item.faculty_name?.match( searchValue ) || item.assignment_created_by?.match( searchValue ) )
        } else {
            return item.department?.match( searchValue ) || item.faculty_name?.match( searchValue ) || item.assignment_created_by?.match( searchValue )
        }
    }

    const removeDeletedAssignment = ( value ) => {
        const newAssignmentList = [...assignments]
        setAssignments( newAssignmentList.filter( a => a.assignment_id !== value.assignment_id ) )
    }

    const removeAppliedFilters = () => {
        setSearchParams( prev => {
            prev.set( 'searchText', '' )
            prev.set( 'selectedInstitute', "all" )
            prev.set( 'selectedDepartment', "all" )
            return prev
        } )
    }

    const getData = useCallback( async () => {
        try {
            setLoading( true )
            const { data: { data } } = await api.audit.getAssignmentsForAuditing( { institute: selectedInstitute !== "all" ? selectedInstitute : "" } )
            setAssignments( data )
            setError( null )
        } catch ( err ) {
            setError( { code: err?.response?.status, message: err?.message?.data?.message || "Something went wrong while fetching assignments." } )
        } finally {
            setLoading( false )
        }
    }, [selectedInstitute] )


    useEffect( () => {
        getData()
    }, [getData] )

    useEffect( () => {
        console.log( selectedAssignment )
    }, [selectedAssignment] )

    return (
        <Card sx={{ flexShrink: 0, display: "flex", flexDirection: "column" }}>

            <AssignmentViewer onAfterDelete={removeDeletedAssignment} onClose={() => setSelectedAssignment( null )} loaded={true} selectedValue={selectedAssignment} open={!submissionViewerState && Boolean( selectedAssignment )} />
            {selectedAssignment && submissionViewerState && <SubmissionsViewer selectedValue={selectedAssignment} handleClose={() => { setSubmissionViewerState( false ); setSelectedAssignment( null ) }} assignmentId={selectedAssignment?.assignment_id} open={submissionViewerState} />}

            <Box padding="20px" display="flex" gap="20px" justifyContent="space-between" >
                <Typography color="primaryDark.main" fontSize="16px" variant='h6'>Assignments</Typography>
                <Button size="small" onClick={() => navigate( "/admin/assignment" )} variant="contained" disableElevation sx={{ height: "33.5px", textTransform: "capitalize", background: "#FFE0E0", border: `1px solid #FF6C8F`, color: "#FF736F", '&:hover': { background: "#FFE0E0", color: "#FF736F" } }} >View Stats</Button>
            </Box >

            {!loading && !error && assignments.filter( filterFunction ).length > 0 && <Box overflow="hidden" borderRadius="10px" bgcolor={palette.contentBg} margin="0 20px 20px 20px">
                <TableContainer sx={{ overflow: "auto", maxHeight: "500px" }} className='custom-scrollbar' >
                    <Table stickyHeader>
                        <TableHeader columns={columns} />
                        <TableBody>
                            {assignments.filter( filterFunction ).slice( page * rowsPerPage, page * rowsPerPage + rowsPerPage ).map( ( row, index ) => {
                                return <TableRow className='table-row' key={row.assignment_id}>
                                    <TableCell sx={{ fontSize: "12px", padding: columns[0].padding || "10px 20px", color: table.color, textTransform: "capitalize" }}><strong>{row.assignment_name}</strong></TableCell>
                                    <TableCell align={columns[1].align || "left"} sx={{ fontSize: "12px", padding: columns[1].padding || "10px 20px", textTransform: "capitalize", color: table.color, }}><Typography fontSize="12px" sx={{ '&:hover': { textDecoration: "underline" } }}><strong>{row.faculty_name?.toLowerCase()}</strong></Typography></TableCell>
                                    <TableCell align={columns[2].align || "left"} sx={{ fontSize: "12px", padding: columns[2].padding || "10px 20px", textTransform: "capitalize", color: table.color, }}><strong>{row.department?.toLowerCase()}</strong></TableCell>
                                    <TableCell align={columns[3].align || "left"} sx={{ fontSize: "12px", padding: columns[3].padding || "10px 20px", color: table.color }}><strong>{row.assignment_subject?.subject_name || "Not available"}</strong></TableCell>
                                    <TableCell align={columns[4].align || "left"} sx={{ fontSize: "12px", padding: columns[4].padding || "10px 20px", color: table.color }}><strong>{formatDate( row.assignment_created_at )}</strong></TableCell>
                                    <TableCell align={columns[5].align || "left"} sx={{ fontSize: "12px", padding: columns[5].padding || "10px 20px", color: table.color }}><strong>{row.submissions}</strong></TableCell>
                                    <TableCell align={columns[6].align || "left"} sx={{ fontSize: "12px", padding: columns[6].padding || "10px 20px", color: table.color }}>
                                        <Box display="flex" justifyContent="flex-end" gap="10px" alignItems="center">
                                            <IconButton onClick={() => {
                                                console.log( "ASSIGNMENT SELECTED FOR VIEWING!" )

                                                setSelectedAssignment( row )
                                            }} color="primary" size="small"><OpenInNew fontSize='small' /></IconButton>
                                            {+row.submissions > 0 && <Tooltip title="View submissions">
                                                <IconButton onClick={() => { setSelectedAssignment( row ); setSubmissionViewerState( true ) }} color="secondary" size="small"><Analytics fontSize='small' /></IconButton>
                                            </Tooltip>}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            } )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    sx={{ background: table.headerBg, position: "sticky", bottom: 0 }}
                    rowsPerPageOptions={[50, 100, 150, 200]}
                    component="div"
                    count={assignments.filter( filterFunction ).length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={( event, val ) => setPage( val )}
                    onRowsPerPageChange={( event ) => {
                        setRowsPerPage( parseInt( event.target.value, 10 ) )
                        setPage( 0 )
                    }}
                />
            </Box>}
            {!loading && !error && assignments.filter( filterFunction ).length === 0 && <Box flexDirection="column" padding="20px" display="flex" alignItems="center" justifyContent="center" gap="20px">
                <img src={noAssignmentImg} height="120px" alt="No assignmets to show" />
                <Typography textAlign="center" variant='subtitle2'>No assignments were found for applied filters!</Typography>
                <Button onClick={removeAppliedFilters} sx={{ textTransform: "capitalize" }} color="secondary" variant="contained" disableElevation>Remove filters</Button>
            </Box>}
            {loading && !error && <DataLoadingSpinner waitingMessage="Loading assignments please wait...." />}
            {error && <TryAgainMessageBlock err={error.message} code={error.code} getData={getData} loading={loading} />}
        </Card>
    )
}

export default AssignmentList
